import { useState } from "react";
import { FileText, Gauge, LayoutDashboard, MessageCircle, Mic, Check, HelpCircle, ArrowRight } from "lucide-react";
import { CvDemo } from "./CvDemo";

const TABS = [
  { id: "cv", label: "CV improvement", icon: FileText },
  { id: "compat", label: "Job Compatibility", icon: Gauge },
  { id: "dashboard", label: "Career Dashboard", icon: LayoutDashboard },
  { id: "kesh", label: "Kesh AI", icon: MessageCircle },
  { id: "interview", label: "Interview Prep", icon: Mic },
];

const Synthetic = ({ note }) => (
  <p className="mt-5 text-sm text-faint" data-testid="preview-synthetic-note">{note || "Example with synthetic data. Not a real candidate."}</p>
);

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="gi-card p-5 sm:p-7">{children}</div>
);

function Compat() {
  const rows = [["Essential qualifications & eligibility", 92], ["Relevant skills", 78], ["Experience alignment", 85], ["Evidence & achievements", 76], ["CV clarity & role relevance", 88]];
  return (
    <Card testid="preview-compat">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="font-display text-lg font-semibold text-ink">Operations Executive · Logistics company</div>
          <div className="text-sm text-body">Job Compatibility Analysis</div>
        </div>
        <span className="font-display text-3xl font-extrabold text-ink" data-testid="preview-compat-overall">84%</span>
      </div>
      <div className="space-y-4">
        {rows.map(([l, s]) => (
          <div key={l}>
            <div className="flex justify-between gap-3 text-[15px] mb-1.5"><span className="text-ink">{l}</span><span className="font-semibold text-ink tabular-nums">{s}%</span></div>
            <div className="h-2 rounded-full bg-subtle"><div className="h-full rounded-full bg-brand" style={{ width: `${s}%` }} /></div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-canvas border border-line p-4"><div className="text-success flex items-center gap-1.5 font-semibold text-sm"><Check className="w-4 h-4" aria-hidden="true" /> Why it fits</div><p className="text-body mt-1.5 text-sm">3 years coordinating warehouse schedules and vendors matches the core duties.</p></div>
        <div className="rounded-xl bg-warning-soft border border-warning/25 p-4"><div className="text-warning flex items-center gap-1.5 font-semibold text-sm"><HelpCircle className="w-4 h-4" aria-hidden="true" /> Needs your input</div><p className="text-body mt-1.5 text-sm">The job asks for budget management. Your CV doesn't mention it. Have you done it?</p></div>
      </div>
      <p className="mt-5 text-sm text-faint leading-relaxed">This score is GetInterviewed's own estimate, not how any employer's ATS ranks candidates. Missing a mandatory requirement caps the overall score.</p>
      <Synthetic />
    </Card>
  );
}

function Dashboard() {
  const cols = [["Applied", ["Ops Executive · FreightCo", "Planner · RetailHub"]], ["Interview", ["Ops Analyst · PortLink"]], ["Offer", []]];
  return (
    <Card testid="preview-dashboard">
      <div className="font-display text-lg font-semibold text-ink mb-1">Good morning, Aisha</div>
      <p className="text-[15px] text-body mb-6">Next step: prepare for your PortLink interview on Thursday.</p>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[["12", "Applications"], ["3", "Interviews"], ["25%", "Interview rate"]].map(([n, l]) => (
          <div key={l} className="rounded-xl bg-canvas border border-line p-3 sm:p-4"><div className="font-display text-xl sm:text-2xl font-bold text-ink">{n}</div><div className="text-xs sm:text-sm text-body">{l}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-1 min-[480px]:grid-cols-3 gap-3">
        {cols.map(([c, items]) => (
          <div key={c} className="rounded-xl border border-line p-3 min-h-[96px]">
            <div className="text-xs font-semibold uppercase tracking-wide text-faint mb-2">{c}</div>
            {items.map((i) => <div key={i} className="text-sm rounded-lg bg-canvas border border-line px-2.5 py-1.5 mb-1.5 text-ink">{i}</div>)}
          </div>
        ))}
      </div>
      <Synthetic note="Example dashboard with synthetic data. Your dashboard only ever shows your own records." />
    </Card>
  );
}

function Kesh() {
  return (
    <Card testid="preview-kesh">
      <div className="flex items-center gap-2.5 mb-5">
        <span className="w-8 h-8 rounded-full bg-kesh text-onbrand flex items-center justify-center text-sm font-bold" aria-hidden="true">K</span>
        <div><div className="font-semibold text-ink leading-tight">Kesh</div><div className="text-xs text-kesh-text">Your AI career companion</div></div>
      </div>
      <div className="space-y-3 text-[15px]">
        <div className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand text-onbrand px-4 py-2.5">I've applied to 20 jobs and had 2 interviews. What should I change?</div></div>
        <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-kesh-soft border border-kesh/15 text-body px-4 py-3 leading-relaxed">
          <div className="font-semibold text-ink">What your records show</div>
          Both interviews came from operations roles (2 of 6). Your 14 marketing applications haven't led to interviews yet. That's a small sample, so treat it as a hint, not proof.
          <div className="font-semibold text-ink mt-2">What to try next</div>
          Focus on operations roles this week, and add your vendor-management results to your CV. Want me to open the Evidence Checker?
        </div>
      </div>
      <button onClick={() => window.dispatchEvent(new Event("kesh-open"))} data-testid="preview-try-kesh" className="mt-6 gi-btn gi-btn-kesh">
        Try Kesh now <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
      <Synthetic note="Example conversation with synthetic data. Kesh only uses your own records when you allow it." />
    </Card>
  );
}

function Interview() {
  return (
    <Card testid="preview-interview">
      <div className="text-xs font-semibold uppercase tracking-wide text-faint mb-2">Question 2 of 6 · Behavioural</div>
      <p className="font-display text-xl font-semibold text-ink">"Tell me about a time you had to handle a supplier delay."</p>
      <div className="mt-5 rounded-xl bg-brand-soft/70 border border-brand/15 p-4 text-[15px] text-body leading-relaxed">
        <div className="text-brand-text font-semibold mb-1">Feedback</div>
        Clear situation and action. Add the <span className="text-ink font-semibold">result</span>: what happened to delivery times or the customer? Then you'll have the full STAR shape (Situation, Task, Action, Result).
      </div>
      <Synthetic note="Example question and feedback. Practice uses your real CV and the job you choose. We never claim to know an employer's confidential questions." />
    </Card>
  );
}

export const ProductPreview = () => {
  const [tab, setTab] = useState("cv");
  return (
    <section id="preview" data-testid="product-preview-section" className="scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-20 sm:py-28">
        <div className="max-w-2xl mb-10">
          <div className="gi-eyebrow mb-3">See it in action</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink">A closer look at GetInterviewed</h2>
          <p className="mt-4 text-lg text-body">Click through the main tools. All examples use made-up data.</p>
        </div>
        <div className="grid lg:grid-cols-[250px_1fr] gap-6 lg:gap-10 items-start">
          <div role="tablist" aria-label="Product preview" className="flex flex-wrap lg:flex-col lg:flex-nowrap gap-2">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`preview-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2.5 px-3.5 lg:px-4 py-2.5 lg:py-3 min-h-[44px] rounded-xl text-sm lg:text-[15px] font-medium whitespace-nowrap border transition-colors text-left ${tab === t.id ? "border-brand/40 bg-brand-soft text-brand-text" : "border-line bg-surface text-ink/80 hover:text-ink hover:border-line-strong/60"}`}>
                <t.icon className="w-4 h-4 shrink-0" aria-hidden="true" /> {t.label}
              </button>
            ))}
          </div>
          <div className="min-w-0 af-fade-up" key={tab} role="tabpanel">
            {tab === "cv" && <div data-testid="preview-cv"><CvDemo /></div>}
            {tab === "compat" && <Compat />}
            {tab === "dashboard" && <Dashboard />}
            {tab === "kesh" && <Kesh />}
            {tab === "interview" && <Interview />}
          </div>
        </div>
      </div>
    </section>
  );
};
